import { Button } from "@mui/material";
import TextField from "@mui/material/TextField";
import makeStyles from "@mui/styles/makeStyles";
import { OLModal } from "components/Tooltips/OLModal";
import { OLTheme } from "contexts/Theme/ThemeProvider";
import { Field, Form, Formik } from "formik";
import { useMessage } from "hooks/intl";

export const useStyles = makeStyles((theme: OLTheme) => ({
  modalDescription: {
    color: theme.gray500,
    marginTop: theme.spacing(-10),
    marginBottom: theme.spacing(24),
    fontSize: "14px",
  },
  inputDialog: {
    width: "100%",
  },
  btnWrapper: {
    display: "flex",
    gap: theme.spacing(12),
  },
  modalButton: {
    marginTop: theme.spacing(12),
  },
}));

interface Props {
  isDialogOpen: boolean;
  closeDialog: () => void;
  dialog: Dialog | null;
  setFieldValue: (value: string) => void;
  currentValue: string;
  multiInput: boolean;
  initialValue?: string;
}

export type Dialog = {
  title: string;
  description?: React.ReactElement | string;
  code: string;
  parameter?: string;
};

export const EffectDialog = ({
  isDialogOpen,
  closeDialog,
  dialog,
  setFieldValue,
  currentValue,
  multiInput,
  initialValue,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();

  const findInjectedValue = (key: string) => {
    switch (dialog?.parameter) {
      case "{{ATTRIBUTE}}":
        return dialog?.code.replace(dialog.parameter, key);
      case "{{LABEL_KEY}}":
        return dialog?.code.replace(dialog.parameter, key);
      default:
        return dialog?.code.replace("('')", `("${key}")`);
    }
  };

  return isDialogOpen && dialog ? (
    <OLModal
      header={dialog.title}
      onClose={() => closeDialog()}
      open={isDialogOpen}
      modalWidth={536}
    >
      <Formik
        initialValues={{ key: initialValue || "" }}
        onSubmit={async (values) => {
          const injectedValue = findInjectedValue(values.key);

          if (multiInput) {
            setFieldValue(`${currentValue} <#>${injectedValue}</#>`);
          } else {
            setFieldValue(`<#>${injectedValue}</#>`);
          }

          closeDialog();
        }}
      >
        {({ handleSubmit, isSubmitting }) => (
          <Form onSubmit={handleSubmit}>
            <div className={classes.modalDescription}>{dialog.description}</div>
            <Field name="key">
              {({ field }: { field: any }) => (
                <TextField
                  {...field}
                  className={`${classes.inputDialog}`}
                  value={field.value ?? ""}
                  variant="outlined"
                  autoComplete="off"
                  label={dialog.title}
                  inputProps={{
                    "data-test-id": "dialog-input",
                    autoComplete: "off",
                    ...field.inputProps,
                  }}
                />
              )}
            </Field>
            <div className={classes.btnWrapper}>
              <Button
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="button"
                variant="outlined"
                onClick={() => closeDialog()}
                data-test-id="dialog-cancel"
              >
                {message({ id: "cancel" })}
              </Button>
              <Button
                className={classes.modalButton}
                color="primary"
                disabled={isSubmitting}
                fullWidth
                type="submit"
                variant="contained"
                data-test-id="dialog-save"
              >
                {message({ id: "save" })}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </OLModal>
  ) : null;
};
